import { useEffect, useState } from "react";
import { Bell, BellOff, Send, Smartphone, Trash2 } from "lucide-react";

import { Spinner, ago, useToast } from "./ui.jsx";
import { api } from "../lib/api.js";

/**
 * Notification settings.
 *
 * The awkward part is iOS, and it is better said out loud than discovered: Safari
 * will not offer push at all until the site has been added to the Home Screen, so
 * a phone visiting in a browser tab sees an explanation rather than a button that
 * cannot work.
 */

function urlBase64ToUint8Array(base64) {
    const padded = (base64 + "=".repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(padded);
    return Uint8Array.from([...raw].map((character) => character.charCodeAt(0)));
}

/** Standalone means launched from the Home Screen rather than in a tab. */
function isStandalone() {
    return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

function isIos() {
    return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export function Notifications() {
    const [data, setData] = useState(null);
    const [permission, setPermission] = useState(typeof Notification === "undefined" ? "unsupported" : Notification.permission);
    const [busy, setBusy] = useState(false);
    const toast = useToast();

    const load = () => api.notifications.get().then(setData).catch(() => setData(null));
    useEffect(() => {
        load();
    }, []);

    if (!data) return <Spinner className="muted" />;

    const supported = "serviceWorker" in navigator && "PushManager" in window && permission !== "unsupported";
    const needsInstall = isIos() && !isStandalone();

    const subscribe = async () => {
        setBusy(true);
        try {
            const granted = await Notification.requestPermission();
            setPermission(granted);
            if (granted !== "granted") {
                toast("Permission was not granted", "error");
                return;
            }

            const registration = await navigator.serviceWorker.ready;
            const existing = await registration.pushManager.getSubscription();
            const subscription = existing ?? (await registration.pushManager.subscribe({
                // Every push must be visible to the user; the browser enforces it.
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(data.publicKey)
            }));

            const json = subscription.toJSON();
            await api.notifications.subscribe({
                endpoint: json.endpoint,
                keys: json.keys,
                label: `${isIos() ? "iPhone" : navigator.platform || "this device"}`
            });
            toast("This device will be notified");
            load();
        } catch (error) {
            toast(error.message, "error");
        } finally {
            setBusy(false);
        }
    };

    const toggle = async (id, enabled) => {
        setData({ ...data, events: data.events.map((event) => (event.id === id ? { ...event, enabled } : event)) });
        try {
            await api.notifications.update({ events: { [id]: enabled } });
        } catch (error) {
            toast(error.message, "error");
            load();
        }
    };

    const setThreshold = async (key, value) => {
        setData({ ...data, thresholds: { ...data.thresholds, [key]: value } });
        try {
            await api.notifications.update({ thresholds: { [key]: value } });
        } catch (error) {
            toast(error.message, "error");
            load();
        }
    };

    return (
        <section className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="section-title">Notifications</h2>
                {data.devices.length > 0 ? (
                    <button
                        className="btn-ghost"
                        onClick={async () => {
                            try {
                                const result = await api.notifications.test();
                                toast(`Sent to ${result.sent} device${result.sent === 1 ? "" : "s"}`);
                            } catch (error) {
                                toast(error.message, "error");
                            }
                        }}
                    >
                        <Send size={14} /> Send a test
                    </button>
                ) : null}
            </div>

            {!data.configured ? (
                <div className="card p-3 text-sm">
                    <p className="flex items-center gap-2 font-medium"><BellOff size={15} className="muted" /> Push is not configured</p>
                    <p className="muted mt-1 text-xs">
                        Set <code className="font-mono">VAPID_PUBLIC_KEY</code> and <code className="font-mono">VAPID_PRIVATE_KEY</code> in{" "}
                        <code className="font-mono">/data/ops/shared/ops.env</code>, then reload ops-api.
                    </p>
                </div>
            ) : needsInstall ? (
                <div className="card p-3 text-sm">
                    <p className="flex items-center gap-2 font-medium"><Smartphone size={15} className="muted" /> Add this to your Home Screen first</p>
                    <p className="muted mt-1 text-xs">
                        Safari only offers notifications to a site that has been installed. Tap Share, then
                        “Add to Home Screen”, and open ops from there — this page will offer to enable them.
                    </p>
                </div>
            ) : !supported ? (
                <div className="card p-3 text-sm">
                    <p className="muted">This browser does not support push notifications.</p>
                </div>
            ) : data.devices.length === 0 || permission !== "granted" ? (
                <div className="card flex flex-wrap items-center justify-between gap-3 p-3 text-sm">
                    <p className="muted">Nothing on this device is subscribed yet.</p>
                    <button className="btn-primary" onClick={subscribe} disabled={busy}>
                        <Bell size={14} /> {busy ? "Asking…" : "Enable on this device"}
                    </button>
                </div>
            ) : null}

            {data.devices.length > 0 ? (
                <div className="card divide-y divide-gray-100 dark:divide-gray-800">
                    {data.devices.map((device) => (
                        <div key={device.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-3 py-2.5 text-sm">
                            <div className="min-w-0">
                                <p className="flex items-center gap-2">
                                    <Smartphone size={14} className="muted shrink-0" />
                                    {device.label}
                                    <code className="muted font-mono text-xs">…{device.fingerprint}</code>
                                </p>
                                <p className="muted text-xs">
                                    added {ago(device.createdAt)}
                                    {device.lastSentAt ? ` · last notified ${ago(device.lastSentAt)}` : " · nothing sent yet"}
                                </p>
                            </div>
                            <button
                                className="btn-danger px-2 py-1"
                                aria-label={`Remove ${device.label}`}
                                onClick={async () => {
                                    await api.notifications.removeDevice(device.id);
                                    toast(`Removed ${device.label}`);
                                    load();
                                }}
                            >
                                <Trash2 size={12} />
                            </button>
                        </div>
                    ))}
                </div>
            ) : null}

            <div className="card divide-y divide-gray-100 dark:divide-gray-800">
                {data.events.map((event) => (
                    <label key={event.id} className="flex cursor-pointer items-center justify-between gap-4 px-3 py-2.5 text-sm">
                        <span className="min-w-0">{event.label}</span>
                        <input
                            type="checkbox"
                            className="size-4 shrink-0"
                            checked={event.enabled}
                            onChange={(check) => toggle(event.id, check.target.checked)}
                        />
                    </label>
                ))}
            </div>

            <div className="card space-y-3 p-3">
                <p className="muted text-xs">
                    A resource has to stay over its limit for {Math.round(data.sustainedSeconds / 60)} minutes before you hear
                    about it — a build pinning the CPU for a moment is not an incident.
                </p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {[
                        { key: "cpuPercent", label: "CPU over", suffix: "%" },
                        { key: "memoryPercent", label: "Memory over", suffix: "%" },
                        { key: "diskPercent", label: "Disk over", suffix: "%" },
                        { key: "certDays", label: "Certificate within", suffix: " days" }
                    ].map((field) => (
                        <div key={field.key}>
                            <label className="label mb-1" htmlFor={`threshold-${field.key}`}>
                                {field.label}
                            </label>
                            <div className="flex items-center gap-2">
                                <input
                                    id={`threshold-${field.key}`}
                                    type="number"
                                    className="input"
                                    value={data.thresholds[field.key]}
                                    min={1}
                                    max={field.key === "certDays" ? 365 : 100}
                                    onChange={(event) => setThreshold(field.key, Number(event.target.value))}
                                />
                                <span className="muted shrink-0 text-xs">{field.suffix.trim()}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
